import { size, compose, entries, map, isEmpty, filter, includes, orderBy } from 'lodash/fp';
import { Component, createRef } from 'react';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import { string } from 'fp-ts';
import { withMewsSrcSet } from '@mews/business-components';
import { FeatureFlagServiceProps, LocalizedText, withFeatureFlagService } from '@mews/framework';
import { KeyValue } from '@mews/enums';
import { Empty, RouteLink } from '@mews-commander/ui';
import { TextKey, RouteName } from '@mews-commander/enums';
import { ApplicationState } from '@mews-commander/types';
import { Avatar, Input, InputValue, ListDivider, ListItem, StackItem, Virtual } from '@optimus-web/core';
import { PLATFORM_ADMIN_ENTERPRISE_ID } from '@mews-commander/constants';
import { normalizeSearchValue, deferFocus } from '@mews-commander/utils';
import { changeEnterprise } from '../../app/actions/profile';
import { SUB_AVATAR_SIZES, AVATAR_RATIO, AVATAR_WIDTH_SET } from '../../../components/commander-ui/constants';
import { getOtherUserMenuAccounts } from '../selectors';
import { MIN_FILTERABLE_ITEMS, MIN_VIRTUALIZED_ITEMS_COUNT } from '../constants';
import { UserMenuContainer, VirtualContainer, ListContainer } from './styles';
import { AccountSelection, UserMenuItem } from './types';

const AvatarWithSrcSet = withMewsSrcSet(Avatar);

type UserMenuListProps = {
    otherAccounts: UserMenuItem[];
    changeEnterprise(account: AccountSelection, url?: string): void;
    close(): void;
    deferFocus(el: unknown, options: unknown): void;
} & FeatureFlagServiceProps;

interface UserMenuListState {
    filterValue: string;
    selectedIndex: number;
    scrollIntoView: boolean;
}

class UserMenuList extends Component<UserMenuListProps, UserMenuListState> {
    state = {
        filterValue: '',
        selectedIndex: -1,
        scrollIntoView: false,
    };

    componentDidMount() {
        if (this.inputRef.current) {
            this.props.deferFocus(this.inputRef.current, { selector: null });
        }
    }

    handleKeyDown = (e: React.KeyboardEvent) => {
        const { key } = e;
        if (isNavigationKey(key)) {
            e.preventDefault();
            let { selectedIndex } = this.state;
            const items = this.filterValues();
            const itemsLength = size(items);

            if (key === KeyValue.ArrowUp) {
                if (selectedIndex > 0 && selectedIndex < itemsLength) {
                    this.setState({ selectedIndex: --selectedIndex });
                } else {
                    selectedIndex = itemsLength - 1;
                    this.setState({ selectedIndex, scrollIntoView: true });
                }
            }

            if (key === KeyValue.ArrowDown) {
                if (selectedIndex >= 0 && selectedIndex < itemsLength - 1) {
                    this.setState({ selectedIndex: ++selectedIndex });
                } else {
                    selectedIndex = 0;
                    this.setState({ selectedIndex, scrollIntoView: true });
                }
            }

            if (key === KeyValue.Enter && this.state.selectedIndex >= 0) {
                const selectedItem = items[this.state.selectedIndex];
                this.handleChangeAccount({
                    profileId: selectedItem.profileId,
                    enterpriseId: selectedItem.enterpriseId,
                });
            }
        }
    };

    handleChangeAccount = ({ profileId, enterpriseId }: AccountSelection) => {
        this.props.changeEnterprise({ profileId, enterpriseId });
        this.props.close();
    };

    handleFilterChange = (e: React.ChangeEvent<HTMLInputElement> | React.MouseEvent, value: InputValue) => {
        if (string.isString(value)) {
            this.setState({ filterValue: value });
        }
    };

    filterValues = () => {
        const { filterValue } = this.state;

        const normalizedFilterValue = normalizeSearchValue(filterValue);
        if (this.isFilterable() && !isEmpty(normalizedFilterValue)) {
            return filter(
                (o) =>
                    normalizedFilterValue === o.enterpriseId ||
                    normalizedFilterValue === o.subscriptionCode ||
                    includes(normalizedFilterValue, normalizeSearchValue(o.name)),
                this.props.otherAccounts
            );
        }
        return this.props.otherAccounts;
    };

    isFilterable = () => size(this.props.otherAccounts) > MIN_FILTERABLE_ITEMS;

    inputRef = createRef<HTMLInputElement>();

    render() {
        const { otherAccounts } = this.props;
        const { filterValue } = this.state;
        const otherAccountsSize = size(otherAccounts);

        if (otherAccountsSize === 0) {
            return null;
        }

        const items = this.filterValues();

        const itemsSize = size(items);
        let content;
        if (itemsSize > 0) {
            content = compose(
                map(([index, item]) => {
                    const avatarProps = {
                        src: item.enterpriseImageUrl,
                        alt: item.enterpriseAltText,
                        title: item.name,
                        sizes: SUB_AVATAR_SIZES,
                        ratio: AVATAR_RATIO,
                        widthSet: AVATAR_WIDTH_SET,
                    };

                    return (
                        <RouteLink
                            key={item.enterpriseId}
                            route={RouteName.DashboardIndex}
                            params={{
                                currentEnterpriseId:
                                    item.enterpriseId === null ? PLATFORM_ADMIN_ENTERPRISE_ID : item.enterpriseId,
                            }}
                            underline={false}
                        >
                            <ListItem
                                value={item.enterpriseId}
                                key={item.enterpriseId}
                                primaryText={item.name}
                                leftIcon={<AvatarWithSrcSet {...avatarProps} size="small" />}
                                focused={Number(index) === this.state.selectedIndex}
                                shouldScrollIntoView={this.state.scrollIntoView}
                            />
                        </RouteLink>
                    );
                }),
                entries,
                orderBy('isPortfolio', 'desc')
            )(items);

            if (itemsSize > MIN_VIRTUALIZED_ITEMS_COUNT) {
                content = (
                    <VirtualContainer>
                        <Virtual defaultItemHeight={40}>{content}</Virtual>
                    </VirtualContainer>
                );
            }
        } else {
            content = <Empty textKey={TextKey.NoMatch} padded />;
        }

        return (
            <UserMenuContainer>
                {this.isFilterable() && (
                    <>
                        <StackItem padding="50">
                            <Input
                                name="usermenu"
                                ref={this.inputRef}
                                value={filterValue}
                                onKeyDown={this.handleKeyDown}
                                onChange={this.handleFilterChange}
                                placeholder={<LocalizedText textKey={TextKey.Search} />}
                            />
                        </StackItem>
                        <ListDivider />
                    </>
                )}
                <ListContainer>{content}</ListContainer>
            </UserMenuContainer>
        );
    }
}

interface OwnProps {
    close(): void;
}
interface StateProps {
    otherAccounts: UserMenuItem[];
}
interface DispatchProps {
    changeEnterprise: (account: AccountSelection, url?: string) => void;
    deferFocus(el: unknown, options: unknown): void;
}

export default connect<StateProps, DispatchProps, OwnProps, ApplicationState>(
    createStructuredSelector<ApplicationState, StateProps>({
        // @ts-expect-error $TSFixMeError: Type 'OutputSelector<ApplicationState, { enterpriseId: { toString: ...; charAt: ...; charCodeAt: ...; concat: ...; index...
        otherAccounts: getOtherUserMenuAccounts,
    }),
    {
        changeEnterprise,
        deferFocus,
    }
)(withFeatureFlagService(UserMenuList));

const isNavigationKey = (key: string) =>
    key === KeyValue.ArrowDown || key === KeyValue.ArrowUp || key === KeyValue.Enter;
